import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { selectQRCode, setQrCode } from "../slices/accessSlice";
import {
	selectIsAnyTransactionActive,
	selectTransactionDescriptor,
	type TransactionDescriptor,
} from "../slices/transactionSlice";
import { decodeQRCode } from "../utils/decodeQRCode";
import { GET_QR_CODE_QUERY } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

interface Props {
	fridgeId: string;
}

// startQr is the raw QR; startAccessId is its decoded access ID, filled in once the async decode at
// start resolves (and re-derived in resolveEnd if the decode hasn't landed yet).
type PendingTransaction = { descriptor: TransactionDescriptor; startQr: string | null; startAccessId: string | null };

const SENTRY_KIND = "QR_TRANSACTION_LIFECYCLE";

// Decode the access ID embedded in a QR (the meaningful identity — see decodeQRCode), null-safe.
const decodeAccessId = (qr: string | null): Promise<string | null> => (qr ? decodeQRCode(qr) : Promise.resolve(null));

// Renders the decoded start/end access IDs into the event message so a rotation reads straight off the
// Sentry event list (start → end); "?" marks an absent or undecodable side.
const accessIdPair = (startAccessId: string | null, endAccessId: string | null): string =>
	`${startAccessId ?? "?"} → ${endAccessId ?? "?"}`;

// The logging/self-heal calls are fire-and-forget (their result feeds nothing synchronous). They handle
// their own errors internally; this just backstops an unexpected rejection to Sentry instead of leaving
// it unhandled — and keeps the call sites free of repeated biome-ignore noVoid pragmas.
const fireAndForget = (promise: Promise<unknown>): void => {
	promise.catch((error: unknown) => Sentry.captureException(error));
};

const tagScope = (scope: Sentry.Scope, phase: "start" | "end", fridgeId: string, descriptor: TransactionDescriptor) => {
	scope.setTag("kind", SENTRY_KIND);
	scope.setTag("qrPhase", phase);
	scope.setTag("transactionId", descriptor.id);
	scope.setTag("transactionType", descriptor.type);
	scope.setExtra("fridgeId", fridgeId);
};

/**
 * Emits a Sentry event for the QR a transaction STARTED with and the QR shown once it ENDED, comparing
 * the ACCESS ID decoded from inside each QR (not the raw image bytes — two PNG renders can carry the
 * same access ID). When the access ID did not change, it self-heals: it pulls the authoritative current
 * code from the backend (network-only) and replaces the one on screen. Covers orders, local refills and
 * remote refills.
 *
 * End resolution fires at the FINISHED edge whenever a QR is still displayed — i.e. for remote refills,
 * which never null the QR (so OrderStep1's `!qrCode` fallback can't fire for them) — so a missed/lagged
 * rotation is healed before the next customer can scan a stale code. Orders and local refills null the
 * QR at start (OrderStep2), so their end resolves a beat later, when OrderStep1's fallback lands the
 * rotated code (the qrCode-change effect). To avoid a false "did not rotate" alert when the live frame
 * merely lags the FINISHED frame, the unchanged path refetches FIRST and only raises the exception if
 * the refetched access ID is ALSO unchanged.
 */
export const useQrCodeLifecycleLogging = ({ fridgeId }: Props) => {
	const dispatch = useAppDispatch();
	const qrCode = useAppSelector(selectQRCode);
	const isActive = useAppSelector(selectIsAnyTransactionActive);
	const descriptor = useAppSelector(selectTransactionDescriptor);
	const [getQrCodeQuery] = useLazyQuery(GET_QR_CODE_QUERY);

	// Mirror the latest descriptor synchronously so the edge effect reads it without re-firing on a
	// mid-flow id change (e.g. an order migrating to a local refill keeps the slot active throughout).
	// Such a cross-kind takeover is deliberately logged as ONE transaction (the slot never goes idle);
	// the next clean idle edge re-syncs the descriptor for the following transaction.
	const descriptorRef = useRef(descriptor);
	descriptorRef.current = descriptor;

	// Mirror the LIVE, null-aware QR so the edge effect can tell the two flows apart at the finished
	// edge: a remote refill still shows its (possibly stale) code (truthy → heal now), while an
	// order/local refill nulled it at start (falsy → defer to OrderStep1's fallback).
	const qrCodeRef = useRef(qrCode);
	qrCodeRef.current = qrCode;

	// The last non-null QR (what was on screen). Surviving the null that OrderStep2 dispatches at a
	// local transaction's start makes the start access ID race-proof.
	const lastDisplayedQrRef = useRef<string | null>(qrCode);
	const startInfoRef = useRef<PendingTransaction | null>(null);
	const awaitingEndRef = useRef<PendingTransaction | null>(null);
	const prevIsActiveRef = useRef<boolean>(isActive);

	// The transactionId we last logged a START for. A fresh active edge carrying the SAME id is an
	// isActive flicker (sparse subscription frames toggling the slot true→false→true mid-transaction),
	// not a new transaction — re-starting it would capture a now-null QR baseline and emit a spurious
	// "rotation unverified". Used to ignore that edge. (Transaction ids are unique, so a genuinely new
	// transaction never matches.)
	const startedTransactionIdRef = useRef<string | null>(null);

	// Pull the authoritative current access code from the backend (network-only, bypassing the cache).
	// Returns the code, or null on a missing field / failed request — the caller classifies and heals.
	const fetchCurrentQrCode = useCallback(async (): Promise<string | null> => {
		try {
			const response = await getQrCodeQuery({ variables: { id: fridgeId }, fetchPolicy: "network-only" });
			return response.data?.fridge?.access?.qrCode?.code ?? null;
		} catch (error) {
			Sentry.captureException(error);
			return null;
		}
	}, [fridgeId, getQrCodeQuery]);

	const logTransactionStart = useCallback(
		async (pending: PendingTransaction) => {
			const accessId = await decodeAccessId(pending.startQr);
			// Mutate the shared pending object so resolveEnd can reuse the decode (it's the same object
			// reference that moves into awaitingEndRef at the finished edge).
			pending.startAccessId = accessId;
			Sentry.withScope((scope) => {
				tagScope(scope, "start", fridgeId, pending.descriptor);
				scope.setTag("accessId", String(accessId));
				scope.setExtra("startAccessId", accessId);
				logToSentry("QR lifecycle: transaction start", "info");
			});
		},
		[fridgeId],
	);

	const resolveEnd = useCallback(
		async (endQr: string | null) => {
			const pending = awaitingEndRef.current;
			if (!pending) {
				return;
			}
			// Clear BEFORE any await so a second invocation (e.g. the qrCode-change effect firing while
			// the refetch is in flight) early-returns — this is what guards against a double-fetch.
			awaitingEndRef.current = null;

			const startAccessId = pending.startAccessId ?? (await decodeAccessId(pending.startQr));
			// A byte-identical render still on screen carries the same access ID — skip the (canvas-heavy)
			// end decode. This is the steady-state no-rotation case (notably a remote refill's unchanged
			// code), so it spares the kiosk GPU process a decode on the hot path.
			const endAccessId = endQr !== null && endQr === pending.startQr ? startAccessId : await decodeAccessId(endQr);

			// A PRESENT QR that decoded to null is a genuine decoder failure (decodeQRCode already reported
			// it) — worth a warning. An ABSENT QR (it was null) is just a missing baseline, e.g. a
			// transaction that began before any QR was shown: rotation can't be verified, but it isn't a
			// decoder problem, so log it at info and don't drown out the real failures.
			const startDecodeFailed = pending.startQr !== null && startAccessId === null;
			const endDecodeFailed = endQr !== null && endAccessId === null;
			if (startDecodeFailed || endDecodeFailed) {
				Sentry.withScope((scope) => {
					tagScope(scope, "end", fridgeId, pending.descriptor);
					// Dynamic access IDs in the message would fragment grouping — pin a stable fingerprint.
					scope.setFingerprint([SENTRY_KIND, "end", "decode-failed"]);
					scope.setExtra("startAccessId", startAccessId);
					scope.setExtra("endAccessId", endAccessId);
					logToSentry(
						`QR lifecycle: transaction end (QR failed to decode, rotation unverified) [${accessIdPair(startAccessId, endAccessId)}]`,
						"warning",
					);
				});
				return;
			}
			if (startAccessId === null || endAccessId === null) {
				Sentry.withScope((scope) => {
					tagScope(scope, "end", fridgeId, pending.descriptor);
					scope.setFingerprint([SENTRY_KIND, "end", "no-baseline"]);
					scope.setExtra("startAccessId", startAccessId);
					scope.setExtra("endAccessId", endAccessId);
					logToSentry(
						`QR lifecycle: transaction end (no QR baseline, rotation unverified) [${accessIdPair(startAccessId, endAccessId)}]`,
						"info",
					);
				});
				return;
			}

			if (startAccessId !== endAccessId) {
				// The access ID on screen already changed → healthy rotation, nothing to heal.
				Sentry.withScope((scope) => {
					tagScope(scope, "end", fridgeId, pending.descriptor);
					scope.setFingerprint([SENTRY_KIND, "end"]);
					scope.setTag("qrChanged", "true");
					scope.setTag("accessId", endAccessId);
					scope.setExtra("startAccessId", startAccessId);
					scope.setExtra("endAccessId", endAccessId);
					logToSentry(`QR lifecycle: transaction end [${accessIdPair(startAccessId, endAccessId)}]`, "info");
				});
				return;
			}

			// The displayed access ID is unchanged. Pull the authoritative code and classify against it: a
			// different access ID means the rotation merely lagged the live frame (heal silently); the same
			// (or a failed fetch) is the true non-rotation anomaly that should alert.
			logToSentry(`QR unchanged after transaction — refetching from backend: ${fridgeId}`, "warning");
			const fetched = await fetchCurrentQrCode();
			const fetchedAccessId = await decodeAccessId(fetched);
			const rotatedAfterRefetch = fetchedAccessId !== null && fetchedAccessId !== startAccessId;

			Sentry.withScope((scope) => {
				tagScope(scope, "end", fridgeId, pending.descriptor);
				scope.setTag("qrChanged", String(rotatedAfterRefetch));
				scope.setTag("accessId", String(rotatedAfterRefetch ? fetchedAccessId : endAccessId));
				scope.setExtra("startAccessId", startAccessId);
				scope.setExtra("endAccessId", endAccessId);
				if (rotatedAfterRefetch) {
					// The backend HAD rotated; the live frame just lagged the FINISHED frame. Not an anomaly.
					scope.setFingerprint([SENTRY_KIND, "end", "lagged"]);
					scope.setExtra("refetchedAccessId", fetchedAccessId);
					logToSentry(
						`QR lifecycle: transaction end (rotation lagged, healed on refetch) [${accessIdPair(startAccessId, fetchedAccessId)}]`,
						"info",
					);
				} else {
					// The access code genuinely did not rotate after the transaction — the alertable anomaly.
					// No custom fingerprint: keep the Error-message grouping (and your Slack alert) stable.
					Sentry.captureException(new Error("QR code did not rotate after transaction"));
				}
			});

			// Always replace the on-screen code with the authoritative refetched one. Even when the access
			// ID is unchanged (the anomaly path), the backend's current code can be a fresh render of the
			// same access ID, so this keeps the kiosk showing the BE's authoritative QR rather than a stale
			// local render. When the bytes already match it collapses to a harmless reducer no-op.
			if (fetched) {
				dispatch(setQrCode(fetched));
			}
		},
		[fridgeId, fetchCurrentQrCode, dispatch],
	);

	useEffect(() => {
		if (qrCode) {
			lastDisplayedQrRef.current = qrCode;
		}
	}, [qrCode]);

	// START / END edges, driven by the running -> idle transition (any transaction kind).
	useEffect(() => {
		const wasActive = prevIsActiveRef.current;
		prevIsActiveRef.current = isActive;

		if (!wasActive && isActive) {
			const descriptor = descriptorRef.current;
			// Ignore an isActive flicker for a transaction we already started (see startedTransactionIdRef):
			// the slot toggling true→false→true mid-transaction must not begin a second lifecycle.
			if (descriptor && descriptor.id === startedTransactionIdRef.current) {
				return;
			}
			// A new transaction is starting. If the previous one's end never resolved (no QR change and
			// no rotation), resolve it now against the QR that was on screen up to this point.
			fireAndForget(resolveEnd(lastDisplayedQrRef.current));

			if (!descriptor) {
				return;
			}
			const pending: PendingTransaction = {
				descriptor: descriptor,
				startQr: lastDisplayedQrRef.current,
				startAccessId: null,
			};
			startInfoRef.current = pending;
			startedTransactionIdRef.current = descriptor.id;
			fireAndForget(logTransactionStart(pending));
			return;
		}

		if (wasActive && !isActive && startInfoRef.current) {
			awaitingEndRef.current = startInfoRef.current;
			startInfoRef.current = null;
			// Eager heal at the finished edge: a remote refill never nulls the QR, so the (possibly
			// stale) code is on screen NOW — resolve immediately so a missed/lagged rotation is
			// refetched before the next customer can scan it, instead of deferring to the next start.
			// Orders/local refills have qrCode === null here (OrderStep2 nulled it at start) → no-op;
			// their end resolves when OrderStep1's fallback lands the rotated code (the effect below).
			if (qrCodeRef.current) {
				fireAndForget(resolveEnd(qrCodeRef.current));
			}
		}
	}, [isActive, resolveEnd, logTransactionStart]);

	// END resolution for orders/local refills: the first non-null QR shown after the transaction ended
	// (their QR was nulled at start, so this fires when OrderStep1's fallback lands the rotated code).
	useEffect(() => {
		if (awaitingEndRef.current && qrCode) {
			fireAndForget(resolveEnd(qrCode));
		}
	}, [qrCode, resolveEnd]);
};
